import { isNull } from "lodash";
import { useCallback } from "react";
import {
  inRange,
  clearNoCreditScoresIfValid,
  parseCompensationValue,
  useLookup,
  getUpfrontPPEFeeValue,
  applyRecursiveRate
} from "./utils";
import { EppsLoanInformation } from "../../../../models/epps-models";
const useTextboxFieldUpdateLogic = () => {
  return useCallback(
    (renderedField, schemaForm, id, other) => {
      switch (renderedField.name) {
        case "govtFeeFactor":
          return (e) => {
            let val = null;
            const govtUpfrontFees = other.govtUpfrontFees;
            const { ltv, vaFirstTimeUse, loanPurpose } = schemaForm.getValues("loanInformation");
            const standardProducts = schemaForm.getValues("standardProducts");
            const specialProducts = schemaForm.getValues("specialProducts");
            const isVA = standardProducts?.includes(3);
            const isVaFundingExempt = schemaForm.getValues(
              "loanInformation.vaFundingFeeExempted"
            );
            if (isVA && isVaFundingExempt) {
              val = 0;
            } else if (Array.isArray(standardProducts || specialProducts) && (standardProducts || specialProducts).length > 0 && ltv) {
              val = getUpfrontPPEFeeValue(
                { standardProducts, specialProducts },
                { govtUpfrontFees, vaFirstTimeUse, loanPurpose, ltv }
              );
              if (standardProducts?.includes(
                EppsLoanInformation.StandardProducts.USDA
              )) {
                applyRecursiveRate(val);
              }
            }
            renderedField.onChange(val);
          };
        case "loanInformation.target":
          return (e) => {
            const targetLabel = schemaForm.getValues(
              "loanInformation.targetLabel"
            );
            const targetValue = parseFloat(e.target.value);
            if (targetLabel) {
              if (isNull(targetValue))
                schemaForm.setError(id, {
                  type: "manual",
                  message: "Required field"
                });
              else if (targetLabel === "Rate" && !inRange(targetValue, 0, 20)) {
                schemaForm.setError(id, {
                  type: "manual",
                  message: "Between 0% - 20%"
                });
              } else {
                schemaForm.clearErrors(id);
              }
            }
            renderedField.onChange(e);
          };
        case "loanInformation.averageRepresentativeCreditScore":
          return (e) => {
            const num = Number(e.target.value);
            const noCreditScores = schemaForm.getValues(
              "borrowerFinancialHistory.noCreditScores"
            );
            if ((num < 300 || num > 850) && (!noCreditScores || noCreditScores === void 0) && e.target.value) {
              schemaForm.setError(id, {
                type: "manual",
                message: "Between 300 and 850"
              });
            } else {
              schemaForm.clearErrors(id);
              clearNoCreditScoresIfValid(num, noCreditScores, schemaForm);
            }
            renderedField.onChange(e);
          };
        case "loanInformation.financedAmount":
          return (e) => {
            const num = Number(e.target.value);
            schemaForm.setValue("loanInformation.feeAmount", num);
            schemaForm.setValue("loanInformation.feeAmountFinanced", num);
            schemaForm.setValue("loanInformation.feeAmountPaidinCash", 0);
            renderedField.onChange(e);
          };
        case "borrowers[0].borrowerFinanancial.creditScore":
          return (e) => {
            const num = Number(e.target.value);
            const noCreditScores = schemaForm.getValues(
              "borrowerFinancialHistory.noCreditScores"
            );
            if ((num < 300 || num > 850) && (!noCreditScores || noCreditScores === void 0) && e.target.value) {
              schemaForm.setError(id, {
                type: "manual",
                message: "Between 300 and 850"
              });
            } else {
              schemaForm.clearErrors(id);
              clearNoCreditScoresIfValid(num, noCreditScores, schemaForm);
            }
            renderedField.onChange(e);
            const all = schemaForm.getValues("borrowers");
            all.slice(1).forEach((_, idx) => {
              schemaForm.setValue(
                `borrowers[${idx + 1}].borrowerFinanancial.creditScore`,
                num
              );
            });
          };
        case "borrowers[0].giftFundsDollar":
          return (e) => {
            const giftFundsPercent = Math.round(
              Math.abs(e.target.value) / schemaForm.getValues("property.value") * 1e5
            ) / 1e3;
            schemaForm.setValue(
              "borrowers[0].giftFundsPercent",
              giftFundsPercent
            );
            schemaForm.setValue(
              "loanInformation.giftForDownPayment",
              e.target.value
            );
            renderedField.onChange(e);
          };
        case "borrowers[0].giftFundsPercent":
          return (e) => {
            const giftFundsDollar = Math.round(
              e.target.value * (schemaForm.getValues("property.value") / 100)
            );
            schemaForm.setValue(
              "borrowers[0].giftFundsDollar",
              giftFundsDollar
            );
            schemaForm.setValue(
              "loanInformation.giftForDownPayment",
              giftFundsDollar
            );
            renderedField.onChange(e);
          };
        case "borrowers[0].sellerDollar":
          return (e) => {
            const sellerPercent = Math.round(
              Math.abs(e.target.value) / schemaForm.getValues("property.value") * 1e5
            ) / 1e3;
            schemaForm.setValue("borrowers[0].sellerPercent", sellerPercent);
            schemaForm.setValue(
              "loanInformation.sellerConsessionForDownPayment",
              e.target.value
            );
            renderedField.onChange(e);
          };
        case "borrowers[0].sellerPercent":
          return (e) => {
            const sellerDollar = Math.round(
              e.target.value * (schemaForm.getValues("property.value") / 100)
            );
            schemaForm.setValue("borrowers[0].sellerDollar", sellerDollar);
            schemaForm.setValue(
              "loanInformation.sellerConsessionForDownPayment",
              sellerDollar
            );
            renderedField.onChange(e);
          };
        default:
          return renderedField.onChange;
      }
    },
    []
  );
};
const useCheckboxFieldUpdateLogic = () => {
  return useCallback(
    (renderedField, schemaForm, isCustomCheckbox) => {
      if (isCustomCheckbox) {
        return () => renderedField.onChange(
          renderedField.value === "True" ? "False" : "True"
        );
      }
      switch (renderedField.name) {
        default:
          return () => renderedField.onChange(!renderedField.value);
      }
    },
    []
  );
};
const useSingleComboboxFieldUpdateLogic = () => {
  const parseValue = (value) => {
    if (!Array.isArray(value)) {
      if (value?.value && !isNaN(Number(value.value))) {
        return parseInt(value.value, 10);
      }
      if (value?.value === "null") {
        return null;
      }
      return value?.value ?? null;
    }
    return value.map(
      (option) => !isNaN(Number(option.value)) ? parseInt(option.value, 10) : option.value
    );
  };
  return useCallback(
    (renderedField, schemaForm, id) => (value) => {
      switch (renderedField.name) {
        case "compensation.model":
          schemaForm.setValue(
            "compensation.model",
            parseCompensationValue(
              !Array.isArray(value) ? value?.value : null
            )
          );
          return renderedField.onChange(parseValue(value));
        case "loanInformation.commitmentType":
          schemaForm.setValue("deliveryType", parseValue(value));
          return renderedField.onChange(parseValue(value));
        case "borrowerFinancialHistory.docTypeEmployment": {
          const parsed = parseValue(value);
          const handleFieldError = (field, message) => {
            if (schemaForm.getValues(field) !== "0") {
              schemaForm.setError(field, { type: "manual", message });
            } else {
              schemaForm.clearErrors(field);
            }
          };
          if (parsed === 0) {
            handleFieldError(
              "borrowerFinancialHistory.docTypeAsset",
              "Must select No Asset for the given Employment Section."
            );
            handleFieldError(
              "borrowerFinancialHistory.docTypeIncome",
              "Must select No Income for the given Employment Section."
            );
          } else {
            schemaForm.clearErrors([
              "borrowerFinancialHistory.docTypeAsset",
              "borrowerFinancialHistory.docTypeIncome"
            ]);
          }
          return renderedField.onChange(parsed?.toString());
        }
        case "borrowerFinancialHistory.docTypeAsset": {
          const parsed = parseValue(value);
          if (schemaForm.getValues(
            "borrowerFinancialHistory.docTypeEmployment"
          ) === "0" && parsed !== 0) {
            schemaForm.setError("borrowerFinancialHistory.docTypeAsset", {
              type: "manual",
              message: "Must select No Asset for the given Employment Section."
            });
          } else {
            schemaForm.clearErrors("borrowerFinancialHistory.docTypeAsset");
          }
          return renderedField.onChange(parsed?.toString());
        }
        case "borrowerFinancialHistory.docTypeIncome": {
          const parsed = parseValue(value);
          if (schemaForm.getValues(
            "borrowerFinancialHistory.docTypeEmployment"
          ) === "0" && parsed !== 0) {
            schemaForm.setError("borrowerFinancialHistory.docTypeIncome", {
              type: "manual",
              message: "Must select No Income for the given Employment Section."
            });
          } else {
            schemaForm.clearErrors("borrowerFinancialHistory.docTypeIncome");
          }
          return renderedField.onChange(parsed?.toString());
        }
        case "property.type": {
          if (parseValue(value) !== 5 && parseValue(value) !== 6 && parseValue(value) !== 7 && parseValue(value) !== 12) {
            schemaForm.setValue("loanInformation.condotel", false);
            schemaForm.setValue(
              "loanInformation.non_warrantable_Condo",
              false
            );
          }
          return renderedField.onChange(parseValue(value));
        }
        default:
          return renderedField.onChange(parseValue(value));
      }
    },
    []
  );
};
const useLoanOfficerFieldUpdateLogic = () => {
  return useCallback(
    (renderedField, schemaForm, id, setLoanOfficer) => (value) => {
      switch (renderedField.name) {
        default:
          setLoanOfficer(value);
          const parsedValue = ["", void 0].includes(value?.value) ? null : value?.value;
          const parsedLogin = ["", void 0].includes(value?.login) ? null : value?.login;
          schemaForm.setValue("loanInformation.loanOfficerId", parsedValue);
          schemaForm.setValue("eppsUserName", parsedLogin);
          return renderedField.onChange(parsedLogin);
      }
    },
    []
  );
};
const useMultiComboboxFieldUpdateLogic = () => {
  const lookupsLoanTerms = useLookup("loanTerms", null);
  const parseValues = (value) => {
    if (Array.isArray(value)) {
      return value.map(
        (option) => !isNaN(Number(option.value)) ? parseInt(option.value, 10) : option.value
      );
    }
    return value;
  };
  return useCallback(
    (renderedField, schemaForm, id) => (value) => {
      switch (renderedField.name) {
        case "standardProducts":
          const govtProducts = /* @__PURE__ */ new Set(["2", "3", "4"]);
          const selectedGovtProducts = (Array.isArray(value) ? value : []).filter((item) => govtProducts.has(item.value));
          if (selectedGovtProducts.length > 1) {
            schemaForm.setError("standardProducts", {
              type: "manual",
              message: "Only one of FHA, VA or USDA can be selected."
            });
          } else if (Array.isArray(value) && value.length === 0 && schemaForm.getValues("specialProducts")?.length === 0) {
            schemaForm.setError("standardProducts", {
              type: "manual",
              message: "Standard Products is required"
            });
          } else {
            schemaForm.clearErrors("standardProducts");
          }
          return renderedField.onChange(parseValues(value));
        default:
          return renderedField.onChange(parseValues(value));
      }
    },
    []
  );
};
export {
  useCheckboxFieldUpdateLogic,
  useLoanOfficerFieldUpdateLogic,
  useMultiComboboxFieldUpdateLogic,
  useSingleComboboxFieldUpdateLogic,
  useTextboxFieldUpdateLogic
};
